import { fabric } from "fabric";
import { isArrow, isElbowArrow } from "./shapeLabel";
import {
  setArrowEndpoints,
  sceneEndpoints,
  ARROW_GEOM_FIELD,
} from "./arrowEndpoints";
import { routeWithObstacles } from "./orthRoute";

// How close two facing ports must be (on the perpendicular axis) to snap into a
// straight run instead of showing a tiny jog.
const ALIGN_TOL = 20;

// Arrow <-> shape binding (eraser.io style). An arrow endpoint can be "bound" to
// a shape by that shape's stable id; when the shape moves or resizes we re-route
// the arrow so the bound end stays glued to the shape's border, facing the other
// end. The re-route funnels through setArrowEndpoints (utils/arrowEndpoints), the
// single arrow-layout entry point.
//
// Bindings live as three persisted fields (see enableBindingPersistence):
//   shape.id                      — stable identity for a binding target
//   arrow.startBinding / .endBinding — the bound shape's id (or undefined)

// --- stable ids -----------------------------------------------------------
let counter = 0;
const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `wb-${Math.random().toString(36).slice(2)}-${(counter += 1)}`;

export const ensureId = (obj) => {
  if (!obj.id) obj.id = genId();
  return obj.id;
};

// A shape that can be a binding target: anything that isn't an arrow, a bare
// line, or loose text. Rects/ellipses/diamonds/polygons, labelled shapes and
// imported icons (groups) all qualify.
const NON_BINDABLE = new Set(["line", "i-text", "text", "textbox"]);
export const isBindable = (o) =>
  !!o && !o.__nonBindable && !isArrow(o) && !NON_BINDABLE.has(o.type);

// How far outside a shape an arrow endpoint can land and still bind — a small
// "on the border" tolerance so dropping right at the edge binds, without
// engaging (and highlighting) while the endpoint is still well away from it.
export const BIND_MARGIN = 8;

// --- geometry -------------------------------------------------------------
// The shape's absolute (scene) axis-aligned bounding box. Good enough for v1
// across rect/ellipse/diamond/polygon and labelled groups.
//
// While a shape is part of an activeSelection (a multi-select drag), fabric v5's
// getBoundingRect(true) returns coords RELATIVE to the selection — the shape's
// own transform matrix doesn't include the parent group. Transform that bbox by
// the group's matrix to recover absolute scene coords, so an arrow bound to a
// shape that is being dragged inside a selection re-routes to the right place.
const sceneBBox = (shape) => {
  const b = shape.getBoundingRect(true, true);
  if (!shape.group) return b;
  const m = shape.group.calcTransformMatrix();
  const tl = fabric.util.transformPoint(new fabric.Point(b.left, b.top), m);
  const br = fabric.util.transformPoint(
    new fabric.Point(b.left + b.width, b.top + b.height),
    m,
  );
  return {
    left: Math.min(tl.x, br.x),
    top: Math.min(tl.y, br.y),
    width: Math.abs(br.x - tl.x),
    height: Math.abs(br.y - tl.y),
  };
};

// The shape's centre in ABSOLUTE scene coords. Derived from the (absolute) bbox
// rather than getCenterPoint() on purpose: for a shape that is momentarily part
// of an activeSelection, getCenterPoint() returns coords relative to the
// selection, which would mis-place a re-routed arrow while the group is dragged.
// The bbox centre equals the geometric centre for our shapes (bbox is symmetric
// about it, even when rotated).
const sceneCenter = (shape) => {
  const b = sceneBBox(shape);
  return { x: b.left + b.width / 2, y: b.top + b.height / 2 };
};

const bboxContainsPoint = (shape, p, margin = 0) => {
  const b = sceneBBox(shape);
  return (
    p.x >= b.left - margin &&
    p.x <= b.left + b.width + margin &&
    p.y >= b.top - margin &&
    p.y <= b.top + b.height + margin
  );
};

// A polygon's vertices in ABSOLUTE scene coords (handles a shape momentarily
// inside an activeSelection by folding in the group's matrix — see sceneBBox).
const scenePolygonPoints = (poly) => {
  const own = poly.calcTransformMatrix();
  const m = poly.group
    ? fabric.util.multiplyTransformMatrices(
        poly.group.calcTransformMatrix(),
        own,
      )
    : own;
  const off = poly.pathOffset || { x: 0, y: 0 };
  return poly.points.map((p) =>
    fabric.util.transformPoint(new fabric.Point(p.x - off.x, p.y - off.y), m),
  );
};

// Where the ray from `c` toward `t` first crosses the polygon outline (nearest
// forward edge intersection), or null if it somehow misses. Lets an arrow touch
// a diamond/hexagon's actual slanted edge instead of its bounding-box corner.
const rayPolygonBorder = (verts, c, t) => {
  const dx = t.x - c.x;
  const dy = t.y - c.y;
  let best = null;
  for (let i = 0; i < verts.length; i += 1) {
    const a = verts[i];
    const b = verts[(i + 1) % verts.length];
    const ex = b.x - a.x;
    const ey = b.y - a.y;
    const den = ex * dy - dx * ey;
    if (Math.abs(den) < 1e-9) continue; // ray parallel to this edge
    const rt = (ex * (a.y - c.y) - ey * (a.x - c.x)) / den; // dist along the ray
    const u = (dx * (a.y - c.y) - dy * (a.x - c.x)) / den; // pos along the edge
    if (rt > 1e-6 && u >= -1e-6 && u <= 1 + 1e-6 && (!best || rt < best.rt)) {
      best = { rt, x: c.x + dx * rt, y: c.y + dy * rt };
    }
  }
  return best ? { x: best.x, y: best.y } : null;
};

// Point on the shape's border along the ray from its centre toward `toward`, so
// the arrow touches the actual outline (not the middle). Ellipses/circles use a
// true ray-ellipse intersection; polygons (diamond/hexagon) use a ray-polygon
// intersection against their real edges; everything else clips the ray to the
// bounding box (exact for rectangles; a close approximation for icons/groups).
export const borderPoint = (shape, toward) => {
  const c = sceneCenter(shape);
  const dx = toward.x - c.x;
  const dy = toward.y - c.y;
  if (dx === 0 && dy === 0) return { x: c.x, y: c.y };
  if (shape.type === "polygon" && Array.isArray(shape.points)) {
    const hit = rayPolygonBorder(scenePolygonPoints(shape), c, toward);
    if (hit) return hit;
  }
  const b = sceneBBox(shape);
  const hw = b.width / 2;
  const hh = b.height / 2;
  const t =
    shape.type === "ellipse" || shape.type === "circle"
      ? // ray-ellipse: scale the direction so it lands exactly on the curve,
        // otherwise the tip stops on the bounding square and leaves a gap.
        1 / Math.hypot(dx / hw, dy / hh)
      : Math.min(
          dx === 0 ? Infinity : hw / Math.abs(dx),
          dy === 0 ? Infinity : hh / Math.abs(dy),
        );
  return { x: c.x + dx * t, y: c.y + dy * t };
};

// --- anchor (which point on the shape the arrow attaches to) --------------
// Store the attach point as a fraction of the shape's half-extents from its
// centre ({fx,fy} in [-1,1]). This is resolution-independent, so the arrow
// keeps attaching to the SAME side/corner as the shape moves or resizes —
// letting an arrow connect at any reachable border point, not just the centre.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

const anchorOf = (shape, scenePoint) => {
  const c = sceneCenter(shape);
  const b = sceneBBox(shape);
  return {
    fx: clamp((scenePoint.x - c.x) / (b.width / 2 || 1), -1, 1),
    fy: clamp((scenePoint.y - c.y) / (b.height / 2 || 1), -1, 1),
  };
};

// The scene point an anchor currently resolves to (centre + fraction*half).
const anchorTarget = (shape, anchor) => {
  const c = sceneCenter(shape);
  const b = sceneBBox(shape);
  return {
    x: c.x + anchor.fx * (b.width / 2),
    y: c.y + anchor.fy * (b.height / 2),
  };
};

// The axis unit vector pointing from `from` toward `to` (dominant axis) — which
// side of a shape faces the other end.
const facingDir = (from, to) => {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  return Math.abs(dx) >= Math.abs(dy)
    ? { x: Math.sign(dx) || 1, y: 0 }
    : { x: 0, y: Math.sign(dy) || 1 };
};

// The midpoint of the shape's edge on side `dir` — an axis-aligned ray from the
// centre hits that edge at its middle (the natural elbow attach point).
const edgeMidpoint = (shape, dir) => {
  const c = sceneCenter(shape);
  return borderPoint(shape, { x: c.x + dir.x * 1e4, y: c.y + dir.y * 1e4 });
};

// Scene bounding boxes of the shapes an elbow must route AROUND — every bindable
// shape (including the arrow's own two ends, so it can't coil back inside them),
// limited to those near the tail→tip region so pathfinding stays cheap.
const obstacleRects = (canvas, tail, tip) => {
  const pad = 220;
  const rx1 = Math.min(tail.x, tip.x) - pad;
  const ry1 = Math.min(tail.y, tip.y) - pad;
  const rx2 = Math.max(tail.x, tip.x) + pad;
  const ry2 = Math.max(tail.y, tip.y) + pad;
  return canvas
    .getObjects()
    .filter((o) => isBindable(o))
    .map((o) => sceneBBox(o))
    .filter(
      (b) =>
        b.left < rx2 &&
        b.left + b.width > rx1 &&
        b.top < ry2 &&
        b.top + b.height > ry1,
    );
};

// --- lookups --------------------------------------------------------------
const shapeById = (canvas, id) =>
  id ? canvas.getObjects().find((o) => o.id === id) || null : null;

export const boundArrows = (canvas, shapeId) =>
  canvas
    .getObjects()
    .filter(
      (o) =>
        isArrow(o) && (o.startBinding === shapeId || o.endBinding === shapeId),
    );

// Current arrow endpoints in scene coords (delegates to the arrow-layout helper,
// which handles both a straight line and an elbow polyline connector).
const arrowEndpointsScene = (arrow) => sceneEndpoints(arrow);

// Scene position of one arrow end ("start" = tail, "end" = tip).
export const arrowEndScene = (arrow, end) => {
  const e = arrowEndpointsScene(arrow);
  return end === "start" ? e.tail : e.tip;
};

// --- re-route -------------------------------------------------------------
// Recompute one arrow's endpoints from its bindings. A bound end anchors to its
// shape's border (facing the other end); an unbound end keeps its position. A
// binding whose shape no longer exists is treated as unbound. Returns true if a
// bound end was re-routed. Pass refit=false while the arrow itself is being
// dragged (see setArrowEndpoints) — the bounds are re-fitted once on drop.
export const rerouteArrow = (canvas, arrow, refit = true) => {
  const startShape = shapeById(canvas, arrow.startBinding);
  const endShape = shapeById(canvas, arrow.endBinding);
  if (!startShape && !endShape) return false;

  const ends = arrowEndpointsScene(arrow);

  // Elbow arrows auto-pick the side of each shape that FACES the other end and
  // attach at that edge's midpoint (eraser.io/Excalidraw). This is dynamic — it
  // ignores where the arrow was first dropped — so moving a shape to the far
  // side just flips the exit side instead of forcing an ugly wrap-around. Each
  // end then exits perpendicular via the recorded startDir/endDir.
  if (isElbowArrow(arrow)) {
    const sc = startShape ? sceneCenter(startShape) : null;
    const ec = endShape ? sceneCenter(endShape) : null;
    const sDir = startShape
      ? facingDir(sc, ec || ends.tip)
      : facingDir(ends.tail, ends.tip);
    const eDir = endShape
      ? facingDir(ec, sc || ends.tail)
      : facingDir(ends.tip, ends.tail);
    const tail = startShape ? edgeMidpoint(startShape, sDir) : { ...ends.tail };
    const tip = endShape ? edgeMidpoint(endShape, eDir) : { ...ends.tip };
    // Opposite-facing ports that are nearly aligned: snap the perpendicular
    // coord equal so a few-px offset doesn't produce a tiny jog.
    if (sDir.x === -eDir.x && sDir.y === -eDir.y) {
      if (sDir.x !== 0 && Math.abs(tail.y - tip.y) <= ALIGN_TOL) {
        const y = (tail.y + tip.y) / 2;
        tail.y = y;
        tip.y = y;
      } else if (sDir.y !== 0 && Math.abs(tail.x - tip.x) <= ALIGN_TOL) {
        const x = (tail.x + tip.x) / 2;
        tail.x = x;
        tip.x = x;
      }
    }
    arrow.startDir = sDir;
    arrow.endDir = eDir;
    // Route around every nearby shape (incl. both endpoints) so the arrow never
    // cuts through a box or coils back inside its own ends. Falls back to the
    // built-in mid-bend when pathfinding can't connect the ports.
    const route = routeWithObstacles(
      tail,
      sDir,
      tip,
      eDir,
      obstacleRects(canvas, tail, tip),
    );
    setArrowEndpoints(arrow, tail, tip, refit, route);
    return true;
  }

  // Straight arrows keep the attach point where they were dropped. Aim each bound
  // end at its stored anchor; a near-centre anchor is ambiguous, so fall back to
  // facing the other end (a clean edge instead of the middle).
  const meaningful = (a) =>
    a && (Math.abs(a.fx) > 0.05 || Math.abs(a.fy) > 0.05);
  const startAim = startShape
    ? meaningful(arrow.startAnchor)
      ? anchorTarget(startShape, arrow.startAnchor)
      : endShape
        ? sceneCenter(endShape)
        : ends.tip
    : null;
  const endAim = endShape
    ? meaningful(arrow.endAnchor)
      ? anchorTarget(endShape, arrow.endAnchor)
      : startShape
        ? sceneCenter(startShape)
        : ends.tail
    : null;
  const tail = startShape ? borderPoint(startShape, startAim) : ends.tail;
  const tip = endShape ? borderPoint(endShape, endAim) : ends.tip;
  arrow.startDir = undefined;
  arrow.endDir = undefined;
  setArrowEndpoints(arrow, tail, tip, refit);
  return true;
};

// --- bind on draw ---------------------------------------------------------
// Topmost bindable shape whose bbox contains `point` (scene coords), skipping
// `exclude` (a single object or an array — e.g. the arrow being drawn plus its
// transient preview line/head, which must not count as binding targets).
export const shapeUnderPoint = (canvas, point, exclude) => {
  const skip = Array.isArray(exclude) ? exclude : exclude ? [exclude] : [];
  const objs = canvas.getObjects();
  for (let i = objs.length - 1; i >= 0; i -= 1) {
    const o = objs[i];
    if (skip.includes(o) || !isBindable(o)) continue;
    if (bboxContainsPoint(o, point, BIND_MARGIN)) return o;
  }
  return null;
};

// Field names for an end ("start" = tail/e1, "end" = tip/e2).
const bindingField = (end) => (end === "start" ? "startBinding" : "endBinding");
const anchorField = (end) => (end === "start" ? "startAnchor" : "endAnchor");

// Bind one end of an arrow to a shape, anchoring at where the endpoint landed.
export const bindEnd = (arrow, end, shape, scenePoint) => {
  arrow[bindingField(end)] = ensureId(shape);
  arrow[anchorField(end)] = anchorOf(shape, scenePoint);
  ensureId(arrow);
};

// Unbind one end (e.g. its endpoint was dragged into empty space). Also clears
// that end's cached exit direction so the router doesn't keep steering by a
// stale edge normal.
export const unbindEnd = (arrow, end) => {
  arrow[bindingField(end)] = undefined;
  arrow[anchorField(end)] = undefined;
  arrow[end === "start" ? "startDir" : "endDir"] = undefined;
};

// After an arrow is drawn, bind whichever end landed on a shape (anchored at the
// drawn point) and snap it to the border. tailScene/tipScene are the draw ends.
export const bindArrowOnDraw = (canvas, arrow, tailScene, tipScene) => {
  const startShape = shapeUnderPoint(canvas, tailScene, arrow);
  const endShape = shapeUnderPoint(canvas, tipScene, arrow);
  if (startShape) bindEnd(arrow, "start", startShape, tailScene);
  if (endShape) bindEnd(arrow, "end", endShape, tipScene);
  if (startShape || endShape) rerouteArrow(canvas, arrow);
  return { startShape, endShape };
};

// --- persistence ----------------------------------------------------------
// Serialise the binding fields on every object so bindings survive reload and
// undo/redo (both go through toObject/loadFromJSON). Idempotent.
export const enableBindingPersistence = () => {
  if (fabric.__bindingPersist) return;
  fabric.__bindingPersist = true;
  const base = fabric.Object.prototype.toObject;
  fabric.Object.prototype.toObject = function (propertiesToInclude) {
    return base.call(this, [
      "id",
      "startBinding",
      "endBinding",
      "startAnchor",
      "endAnchor",
      ARROW_GEOM_FIELD, // logical endpoints — the arrow's stored geometry
      ...(propertiesToInclude || []),
    ]);
  };
};
